// The Start / End Date cells' popover body: one DateRangePicker covering both dates, with Update and
// Cancel (spec 008 §2.2). One range serves both cells, seeded with whichever dates the row already has
// — the Add-Dates modal's job, relocated.
//
// buildAddDatesInput is the mapping the modal used. Validation is NOT inherited with it: neither the
// picker nor CreateOpportunityModal enforces start-before-end, and the picker cannot represent a
// half-open range at all, so the ordering and completeness guards below are this module's own.
import React, {useState} from 'react';
import {Alert, Button, DateRangePicker, EMPHASIS, Label} from '@forecast-it/design-system';
import Styled from 'styled-components';
import Util from '../../../shared/util/util';
import UpdateProjectMutation from '../../../../mutations/update_project_mutation.modern';
import {createToast} from '../../../shared/components/toasts/toast';
import {partsToIso, buildAddDatesInput, isRangeInverted, type DateInterval} from '../../opportunityDateInput';
import type {OpportunityNode} from '../../opportunityTypes';

export interface ProjectDatesEditorProps {
	node: OpportunityNode;
	onCommit: () => void;
	close: () => void;
	datesLabel: string; // "Project Dates"
	updateLabel: string;
	cancelLabel: string;
	toast: string; // the project-named success copy
	invertedLabel: string; // shown when the end date lands before the start
	incompleteLabel: string; // shown when only one half of the range survives an edit
}

const Body = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	/* The card sizes itself to the picker rather than the other way round.
	   DateRangePicker's own width token for 'flex' is the empty string, i.e. no width at all, so the
	   control fills whatever it is given - but its date segments and calendar icon are flex items with
	   the default min-width:auto, so they refuse to shrink below their intrinsic width and simply spill
	   out of a card that is too narrow. Any fixed px here is a guess that breaks the moment the date
	   format is longer (a different locale, or a four-digit year in a wider font), which is how the
	   icon ended up outside the card twice. fit-content shrink-wraps the widest child, so the card
	   cannot be narrower than the thing it contains; min-width keeps it from looking mean when the
	   dates are short, and max-width stops a pathological locale making it absurd. */
	box-sizing: border-box;
	width: fit-content;
	min-width: 320px;
	max-width: 480px;
	padding: 16px;
`;
const Actions = Styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 8px;
`;

export const ProjectDatesEditor = ({
	node,
	onCommit,
	close,
	datesLabel,
	updateLabel,
	cancelLabel,
	toast,
	invertedLabel,
	incompleteLabel,
}: ProjectDatesEditorProps): JSX.Element => {
	const initial: DateInterval = {
		startDate: partsToIso(node.projectStartYear, node.projectStartMonth, node.projectStartDay),
		endDate: partsToIso(node.projectEndYear, node.projectEndMonth, node.projectEndDay),
	};
	const [interval, setInterval] = useState<DateInterval>(initial);
	// Guards against a rapid double-click firing two update mutations.
	const [saving, setSaving] = useState(false);

	// Nothing to commit until the range differs from what the row already had.
	const unchanged = interval.startDate === initial.startDate && interval.endDate === initial.endDate;
	// The picker's segments are keyboard-editable and will happily put the end before the start, which
	// its calendar popup cannot. The design system marks the field invalid but keeps that to itself, so
	// the ordering is compared here - otherwise Update stays live and commits a backwards range that
	// service-company accepts and stores.
	const inverted = isRangeInverted(interval);
	// The picker cannot express a half-open range. @react-stately's useDateRangePickerState hands its
	// onChange a null the moment either half goes incomplete, and the design system's
	// useDateRangePickerIsoDateState maps that null to {startDate: undefined, endDate: undefined} — so
	// clearing the end date reports the START as gone too, and committing that sends six nulls that
	// blank the date the user never touched (service-company drops baselineTarget along with them).
	// An incomplete range is the state you pass through while filling a row in, not something to save.
	const incomplete = !interval.startDate || !interval.endDate;

	const save = () => {
		if (unchanged || inverted || incomplete || saving) return;
		setSaving(true);
		Util.CommitMutation(UpdateProjectMutation, buildAddDatesInput(node.id, interval), () => {
			// Same full-width success banner the Add-Dates modal showed (DS SUCCESS Alert via the
			// toaster's "banner" variant).
			createToast({duration: 5000, variant: 'banner', message: <Alert type={Alert.TYPE.SUCCESS} text={toast} />});
			onCommit();
		});
		close();
	};

	return (
		<Body data-cy="project-dates-editor">
			<Label>{datesLabel}</Label>
			{/* DateRangePicker computes `fixedWidth = width !== 'flex'`, so leaving width unset renders it at a
			    fixed intrinsic size wider than this popover's content box - the trailing calendar icon then
			    hangs outside the card. 'flex' makes it fill the width it is given instead. */}
			<DateRangePicker
				data-cy="inline-dates-range"
				aria-label={datesLabel}
				width="flex"
				startDate={interval.startDate}
				endDate={interval.endDate}
				onChange={(next: DateInterval) => setInterval({startDate: next.startDate, endDate: next.endDate})}
			/>
			{/* A disabled Update with no reason reads as a broken button. */}
			{/* data-cy sits on the wrapper: the design-system Alert destructures the props it knows and
			    drops the rest, so an attribute passed to it never reaches the DOM. */}
			{inverted ? (
				<span data-cy="inline-dates-inverted">
					<Alert type={Alert.TYPE.WARNING} text={invertedLabel} />
				</span>
			) : incomplete && !unchanged ? (
				<span data-cy="inline-dates-incomplete">
					<Alert type={Alert.TYPE.WARNING} text={incompleteLabel} />
				</span>
			) : null}
			<Actions>
				<Button emphasis={EMPHASIS.LOW} data-cy="inline-dates-cancel" onPress={close}>
					{cancelLabel}
				</Button>
				<Button
					emphasis={EMPHASIS.HIGH}
					data-cy="inline-dates-submit"
					disabled={unchanged || inverted || incomplete || saving}
					onPress={save}
				>
					{updateLabel}
				</Button>
			</Actions>
		</Body>
	);
};

export default ProjectDatesEditor;
